import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';
import { toast } from '../shared/Toast';

const TYPE_COLORS = {
  'Tesouro Direto': 'var(--gold)',
  'CDB': '#e24b4a',
  'FII': '#1d9e75',
  'Ações': '#378ADD',
  'LCI/LCA': '#854F0B',
  'Cripto': '#993556',
  'Capitalização': '#7b5ea7',
  'Consórcio': '#0F6E56',
  'ETF': '#533F91',
};
const TYPES = Object.keys(TYPE_COLORS);

// ── Alocação Alvo ───────────────────────────────────────────────────────
// O usuário define quanto % da carteira quer em cada tipo; o card compara
// com a posição real e diz onde o próximo aporte deveria ir pra rebalancear.
function AllocationModal({ targets, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const f = {};
    TYPES.forEach(t => { f[t] = targets?.[t] ?? ''; });
    return f;
  });
  const sum = TYPES.reduce((s, t) => s + (parseFloat(form[t]) || 0), 0);

  const save = () => {
    if (Math.abs(sum - 100) > 0.01 && sum !== 0) { toast.error(`A soma precisa dar 100% (está em ${sum.toFixed(0)}%).`); return; }
    const clean = {};
    TYPES.forEach(t => { const v = parseFloat(form[t]) || 0; if (v > 0) clean[t] = v; });
    onSave(sum === 0 ? null : clean);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">Alocação alvo da carteira<button className="close-btn" onClick={onClose}>×</button></div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
          Defina quanto % você quer em cada tipo. Deixe em branco o que não usa — a soma precisa dar 100%. Pra desligar, zere tudo.
        </div>
        {TYPES.map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: TYPE_COLORS[t], flexShrink: 0 }}></span>
            <div style={{ flex: 1, fontSize: 13 }}>{t}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input className="form-control" type="number" min="0" max="100" value={form[t]} placeholder="0"
                onChange={e => setForm(f => ({ ...f, [t]: e.target.value }))}
                style={{ width: 70, padding: '6px 8px', textAlign: 'right' }} />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
            </div>
          </div>
        ))}
        <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, margin: '10px 0 14px', color: Math.abs(sum - 100) <= 0.01 ? 'var(--green)' : sum === 0 ? 'var(--muted)' : 'var(--red)' }}>
          Soma: {sum.toFixed(0)}%
        </div>
        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}><i className="fas fa-check"></i> Salvar</button>
      </div>
    </div>
  );
}

export function AllocationCard() {
  const { state, dispatch } = useApp();
  const [modal, setModal] = useState(false);
  const [aporte, setAporte] = useState('500');
  const targets = state.investAllocation;
  const investments = state.investments || [];

  const curByType = useMemo(() => {
    const m = {};
    investments.forEach(i => { m[i.type] = (m[i.type] || 0) + toBRL(i.current || 0, i.currency, state.usdRate); });
    return m;
  }, [investments, state.usdRate]);
  const total = Object.values(curByType).reduce((s, v) => s + v, 0);

  if (investments.length === 0) return null;

  const saveTargets = (t) => dispatch({ type: 'SET_INVEST_ALLOCATION', payload: t });

  if (!targets) {
    return (
      <>
        <div className="card" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(83,63,145,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-bullseye" style={{ color: '#533F91', fontSize: 15 }}></i>
          </div>
          <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>
            <strong style={{ color: 'var(--text)' }}>Alocação alvo:</strong> defina quanto % quer em cada tipo de ativo e o app te diz onde aportar pra manter o equilíbrio.
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setModal(true)}>Definir</button>
        </div>
        {modal && <AllocationModal targets={null} onClose={() => setModal(false)} onSave={saveTargets} />}
      </>
    );
  }

  // Sugestão de rebalanceamento: distribui o aporte proporcionalmente ao que
  // falta em cada tipo pra atingir o alvo (considerando a carteira PÓS-aporte)
  const aporteVal = parseFloat(aporte) || 0;
  const futureTotal = total + aporteVal;
  const gaps = Object.keys(targets).map(t => ({
    type: t,
    gap: Math.max(0, (targets[t] / 100) * futureTotal - (curByType[t] || 0)),
  }));
  const sumGaps = gaps.reduce((s, g) => s + g.gap, 0);
  const sugestoes = sumGaps > 0 && aporteVal > 0
    ? gaps.filter(g => g.gap > 0).map(g => ({ type: g.type, value: aporteVal * g.gap / sumGaps })).sort((a, b) => b.value - a.value)
    : [];

  const rows = [...new Set([...Object.keys(targets), ...Object.keys(curByType)])];

  return (
    <div className="card" style={{ marginBottom: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(83,63,145,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-bullseye" style={{ color: '#533F91', fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Alocação Alvo</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>posição atual vs. onde você quer chegar</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setModal(true)}><i className="fas fa-pen"></i> Editar</button>
      </div>

      {rows.map(t => {
        const cur = total > 0 ? (curByType[t] || 0) / total * 100 : 0;
        const alvo = targets[t] || 0;
        const desvio = cur - alvo;
        const color = TYPE_COLORS[t] || '#888';
        return (
          <div key={t} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ fontWeight: 600 }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6 }}></span>{t}</span>
              <span style={{ color: 'var(--muted)' }}>
                {cur.toFixed(0)}% de {alvo}%
                <strong style={{ marginLeft: 6, color: Math.abs(desvio) <= 3 ? 'var(--green)' : 'var(--gold)' }}>
                  {Math.abs(desvio) <= 3 ? '✓' : desvio > 0 ? `+${desvio.toFixed(0)}pp` : `${desvio.toFixed(0)}pp`}
                </strong>
              </span>
            </div>
            <div style={{ background: 'var(--surface2)', borderRadius: 99, height: 8, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, width: `${Math.min(100, cur)}%`, background: color, opacity: 0.85, borderRadius: 99 }}></div>
              {alvo > 0 && alvo < 100 && <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${alvo}%`, width: 2, background: 'var(--text)', opacity: 0.5 }}></div>}
            </div>
          </div>
        );
      })}

      {/* Sugestão de aporte */}
      <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(83,63,145,0.06)', border: '1px solid rgba(83,63,145,0.2)', borderRadius: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: sugestoes.length ? 8 : 0, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>Se eu aportar</span>
          <input className="form-control" type="number" min="0" value={aporte} onChange={e => setAporte(e.target.value)} style={{ width: 90, padding: '4px 8px' }} />
          <span style={{ fontWeight: 600 }}>reais, onde coloco?</span>
        </div>
        {aporteVal > 0 && (sugestoes.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>🎯 Carteira no alvo — aporte na proporção da alocação que preferir.</div>
        ) : (
          sugestoes.map(s => (
            <div key={s.type} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span><i className="fas fa-arrow-right" style={{ fontSize: 10, marginRight: 6, color: '#533F91' }}></i>{s.type}</span>
              <strong>{formatCurrency(s.value)}</strong>
            </div>
          ))
        ))}
      </div>

      {modal && <AllocationModal targets={targets} onClose={() => setModal(false)} onSave={saveTargets} />}
    </div>
  );
}

// ── Meta de Renda Passiva ───────────────────────────────────────────────
// "Quero R$ X/mês de dividendos" — compara com a média líquida dos últimos
// 6 meses (proventos lançados menos imposto retido, US$ convertido).
function PassiveGoalModal({ goal, onClose, onSave }) {
  const [value, setValue] = useState(goal || '');
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">Meta de renda passiva<button className="close-btn" onClick={onClose}>×</button></div>
        <div className="form-group">
          <label className="form-label">Quanto você quer receber de proventos por mês (R$)?</label>
          <input className="form-control" type="number" min="0" step="10" value={value} onChange={e => setValue(e.target.value)} placeholder="Ex: 500" autoFocus />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Zere pra remover a meta.</div>
        </div>
        <button className="btn btn-primary btn-full" onClick={() => { onSave(parseFloat(value) || 0); onClose(); }} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Salvar
        </button>
      </div>
    </div>
  );
}

export function PassiveIncomeCard() {
  const { state, dispatch } = useApp();
  const [modal, setModal] = useState(false);
  const goal = state.passiveIncomeGoal || 0;
  const investments = state.investments || [];

  // Média mensal líquida dos últimos 6 meses (mesma detecção do gráfico de proventos)
  const mediaMensal = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const liquido = state.transactions
      .filter(t =>
        ((t.type === 'income' && t.categoryId === 'inv' && /^(Dividendo|JCP|Rendimento)/.test(t.description || '')) ||
         (t.type === 'expense' && /^Imposto retido — /.test(t.description || ''))) &&
        months.some(m => t.date?.startsWith(m)))
      .reduce((s, t) => s + (t.type === 'expense' ? -1 : 1) * toBRL(t.value, t.currency, state.usdRate), 0);
    return liquido / 6;
  }, [state.transactions, state.usdRate]);

  if (investments.length === 0) return null;

  const save = (v) => dispatch({ type: 'SET_PASSIVE_GOAL', payload: v });

  if (!goal) {
    return (
      <>
        <div className="card" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(var(--gold-rgb),0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-umbrella-beach" style={{ color: 'var(--gold)', fontSize: 15 }}></i>
          </div>
          <div style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>
            <strong style={{ color: 'var(--text)' }}>Meta de renda passiva:</strong> defina quanto quer receber de proventos por mês e acompanhe o progresso.
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setModal(true)}>Definir</button>
        </div>
        {modal && <PassiveGoalModal goal={0} onClose={() => setModal(false)} onSave={save} />}
      </>
    );
  }

  const pct = Math.min(100, mediaMensal / goal * 100);
  return (
    <div className="card" style={{ marginBottom: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(var(--gold-rgb),0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-umbrella-beach" style={{ color: 'var(--gold)', fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Meta de Renda Passiva</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>média líquida dos últimos 6 meses vs. sua meta</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setModal(true)}><i className="fas fa-pen"></i> Editar</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--gold)' }}>{formatCurrency(mediaMensal)}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>/mês</span></div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>meta: <strong>{formatCurrency(goal)}/mês</strong></div>
      </div>
      <div style={{ background: 'var(--surface2)', borderRadius: 99, height: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: pct >= 100 ? 'var(--green)' : 'linear-gradient(90deg,#8B6914,var(--gold))', transition: 'width 0.6s' }}></div>
      </div>
      <div style={{ fontSize: 12, marginTop: 6, color: pct >= 100 ? 'var(--green)' : 'var(--muted)', fontWeight: pct >= 100 ? 700 : 400 }}>
        {pct >= 100
          ? '🎉 Meta atingida — sua carteira já paga isso por mês!'
          : `${pct.toFixed(0)}% da meta — faltam ${formatCurrency(Math.max(0, goal - mediaMensal))}/mês de proventos.`}
      </div>

      {modal && <PassiveGoalModal goal={goal} onClose={() => setModal(false)} onSave={save} />}
    </div>
  );
}
